import { useQuery } from '@apollo/client';
import { Loader, TextInput } from '@unmind/design-system';
import { tracking } from 'App/Tracking';
import { practitionerCountriesQuery } from 'Services/BeGateway/Practitioner/__generated__/practitionerCountriesQuery';
import { PRACTITIONER_COUNTRIES_QUERY } from 'Services/BeGateway/Practitioner/practitioner.services';
import { SingleSelect } from 'Shared/Form/SingleSelect';
import {
  formatCountryOptions,
  formatStateOptions,
  useAvailableLanguages,
} from 'Book/shared';
import { countryRequiresState, useUserLocation } from 'Account/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BEGatewayQueryContext } from 'utils/apollo';
import {
  MatchingAction,
  MatchingQuestionKeys,
  MatchingStateResponse,
  MatchingStateType,
} from '../../state';
import {
  AddIcon,
  AddLanguageButton,
  AddLanguageText,
  CloseButton,
  CloseButtonContainer,
  CloseIcon,
  InputLegend,
  LanguageSelectionContainer,
  QuestionBodyText,
  QuestionCTAButton,
  QuestionCounter,
  QuestionResponseContainer,
  QuestionTextContainer,
  QuestionTitleTextLabel,
  QuestionnaireForm,
  ResponseFieldset,
} from '../../styles';

function questionnaireLocationIsValid(
  country: string,
  region: string | undefined,
  hasValidLocationData: boolean,
) {
  const isValidNewLocation = countryRequiresState(country)
    ? !!region && !!country
    : !!country;

  return country ? isValidNewLocation : hasValidLocationData;
}

interface Props {
  currentQuestion: number;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<number>>;
  questionResponses: MatchingStateType['questionResponses'];
  addedLanguagesCount: number;
  setLanguageCount: React.Dispatch<React.SetStateAction<number>>;
  setMatchingQuestionState(s: MatchingAction): void;
  onQuestionnaireSubmit(): void;
  questionnaireSubmissionInFlight: boolean;
}

export function createMatchingQuestionKeys() {
  return Object.keys(MatchingQuestionKeys) as MatchingQuestionKeys[];
}

export function Form({
  currentQuestion,
  setCurrentQuestion,
  questionResponses,
  addedLanguagesCount,
  setLanguageCount,
  setMatchingQuestionState,
  onQuestionnaireSubmit,
  questionnaireSubmissionInFlight,
}: Readonly<Props>) {
  const { t } = useTranslation('book', {
    keyPrefix: 'therapyAndCoaching.matches.questionnaire',
  });
  const { t: tLocation } = useTranslation('book', {
    keyPrefix: 'locationModal.fields',
  });

  const { userLocationData, hasValidLocationData } = useUserLocation();
  const {
    data: countriesQueryData,
    loading: practitionerCountriesDataLoading,
  } = useQuery<practitionerCountriesQuery>(PRACTITIONER_COUNTRIES_QUERY, {
    ...BEGatewayQueryContext,
  });
  const { country, region } =
    questionResponses[MatchingQuestionKeys.where_location];
  const regionOptions = formatStateOptions(country?.value);
  const { availableLanguages } = useAvailableLanguages({
    country,
    region,
  });
  const questionKeys = createMatchingQuestionKeys();
  const questionKey = questionKeys[currentQuestion];
  const questionsCount = questionKeys.length;
  const locationIsValid = questionnaireLocationIsValid(
    country?.value,
    region?.value,
    hasValidLocationData,
  );
  const remainingLanguages = availableLanguages.filter(
    language =>
      !questionResponses[MatchingQuestionKeys.what_language].some(
        selectedLanguage => selectedLanguage?.value === language.value,
      ),
  );
  const countryData = countriesQueryData?.practitionerCountries
    ? formatCountryOptions(countriesQueryData.practitionerCountries)
    : [];
  const allRequestedLanguagesSelected =
    questionResponses[MatchingQuestionKeys.what_language].length ===
    addedLanguagesCount;
  const nextButtonDisabled =
    (questionKey === MatchingQuestionKeys.where_location && !locationIsValid) ||
    (questionKey === MatchingQuestionKeys.what_language &&
      !allRequestedLanguagesSelected);

  const handleLanguageSelect = (
    index: number,
    language: { value: string; label: string },
  ) => {
    const newSelectedLanguages = [
      ...questionResponses[MatchingQuestionKeys.what_language],
    ];
    newSelectedLanguages[index] = language;
    setMatchingQuestionState({
      type: MatchingStateResponse[MatchingQuestionKeys.what_language],
      payload: newSelectedLanguages,
    });
  };

  const handleNextQuestionClick = () => {
    tracking.track('talk-matching-question-responded', {
      questionKey,
    });
    setCurrentQuestion(currentQuestion + 1);
  };

  return (
    <QuestionnaireForm>
      <QuestionCounter>
        {currentQuestion + 1}/{questionsCount}
      </QuestionCounter>
      <QuestionTextContainer>
        <QuestionTitleTextLabel
          aria-hidden={questionKey === MatchingQuestionKeys.what_language}
          htmlFor={`question-${questionKey}`}
        >
          {t(`questions.${questionKey}.title`)}
        </QuestionTitleTextLabel>
        <QuestionBodyText>
          {t(`questions.${questionKey}.body`)}
        </QuestionBodyText>
      </QuestionTextContainer>
      <QuestionResponseContainer>
        {questionKey === MatchingQuestionKeys.sessions_for && (
          <TextInput
            id={`question-${MatchingQuestionKeys.sessions_for}`}
            name="matching-text-input"
            size="medium"
            multiline
            rows={5}
            autoComplete="off"
            value={questionResponses[MatchingQuestionKeys.sessions_for]}
            onChange={e => {
              setMatchingQuestionState({
                type: MatchingStateResponse[MatchingQuestionKeys.sessions_for],
                payload: e.target.value,
              });
            }}
          />
        )}
        {questionKey === MatchingQuestionKeys.where_location &&
          (practitionerCountriesDataLoading ? (
            <Loader />
          ) : (
            <ResponseFieldset>
              <InputLegend hidden>{t('location')}</InputLegend>
              <SingleSelect
                inputId="country_select"
                options={countryData}
                placeholder={tLocation('country')}
                onChange={(e: { value: string; label: string }) => {
                  setMatchingQuestionState({
                    type: MatchingStateResponse[
                      MatchingQuestionKeys.where_location
                    ],
                    payload: {
                      country: e,
                      region: {
                        value: '',
                        label: tLocation('region'),
                      },
                    },
                  });
                }}
                value={
                  !!country.value
                    ? country
                    : countryData.find(
                        c => c.value === userLocationData?.user?.country?.value,
                      )
                }
              />
              {countryRequiresState(country?.value) && (
                <SingleSelect
                  inputId="region_select"
                  options={regionOptions}
                  placeholder={tLocation('region')}
                  onChange={(e: { value: string; label: string }) => {
                    setMatchingQuestionState({
                      type: MatchingStateResponse[
                        MatchingQuestionKeys.where_location
                      ],
                      payload: {
                        country:
                          questionResponses[MatchingQuestionKeys.where_location]
                            .country,
                        region: e,
                      },
                    });
                  }}
                  value={regionOptions.find(s => s.value === region?.value)}
                />
              )}
            </ResponseFieldset>
          ))}

        {questionKey === MatchingQuestionKeys.what_language &&
          addedLanguagesCount > 0 && (
            <>
              <ResponseFieldset>
                <InputLegend hidden>
                  {t(`questions.${questionKey}.title`)}{' '}
                  {t(`questions.${questionKey}.body`)}
                </InputLegend>
                {[...Array(addedLanguagesCount)].map((_, index) => (
                  <LanguageSelectionContainer key={`lang-${index}`}>
                    <SingleSelect
                      placeholder={t('language.label')}
                      inputId={`selected-language-${index}`}
                      options={remainingLanguages}
                      onChange={(e: { value: string; label: string }) =>
                        handleLanguageSelect(index, e)
                      }
                      value={
                        questionResponses[MatchingQuestionKeys.what_language][
                          index
                        ]
                      }
                    />
                    <CloseButtonContainer>
                      {addedLanguagesCount > 1 && index >= 1 ? (
                        <CloseButton
                          onClick={() => {
                            const newSelectedLanguages = questionResponses[
                              MatchingQuestionKeys.what_language
                            ].filter((_l, i) => i !== index);

                            setMatchingQuestionState({
                              type: MatchingStateResponse[
                                MatchingQuestionKeys.what_language
                              ],
                              payload: newSelectedLanguages,
                            });
                            setLanguageCount(addedLanguagesCount - 1);
                          }}
                          aria-label={t('close')}
                          disabled={addedLanguagesCount === 1}
                        >
                          <CloseIcon />
                        </CloseButton>
                      ) : null}
                    </CloseButtonContainer>
                  </LanguageSelectionContainer>
                ))}
              </ResponseFieldset>

              <AddLanguageButton
                onClick={() => setLanguageCount(addedLanguagesCount + 1)}
                disabled={
                  addedLanguagesCount === remainingLanguages.length ||
                  addedLanguagesCount === 3 ||
                  !Boolean(
                    questionResponses[MatchingQuestionKeys.what_language][
                      addedLanguagesCount - 1
                    ]?.value,
                  )
                }
              >
                <AddLanguageText>{t('language.add')}</AddLanguageText>
                <AddIcon />
              </AddLanguageButton>
            </>
          )}
        {currentQuestion !== questionsCount - 1 ? (
          <QuestionCTAButton
            label={t('ctas.next')}
            type="button"
            onClick={handleNextQuestionClick}
            disabled={nextButtonDisabled}
          />
        ) : (
          <QuestionCTAButton
            label={t('ctas.submit')}
            type="submit"
            disabled={
              questionnaireSubmissionInFlight ||
              !questionResponses[MatchingQuestionKeys.sessions_for]
            }
            onClick={onQuestionnaireSubmit}
          />
        )}
      </QuestionResponseContainer>
    </QuestionnaireForm>
  );
}

export default Form;
